import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Modal } from '@shared/ui/Modal';
import { Button } from '@shared/ui/Button';
import { Combobox, type ComboboxOption } from '@shared/ui/Combobox';
import { Field, Input, Label, Select } from '@shared/ui/Input';
import {
  kadryErrorCode,
  kadryErrorDetails,
  type KadryCreateRequest,
  type KadryHiringRequest,
  type KadryPatchRequest,
  type KadryStaffMember,
  type KadryWorkSite,
} from '@entities/kadry';
import {
  KadryErrorBanner,
  KadryFieldError,
  KadryFieldGrid,
  KadryFieldHint,
  KadryFormActions,
  KadryTextArea,
  kadryParseNumber,
} from '../ui';
import { ScheduleCost } from './ScheduleCost';

interface FormState {
  title: string;
  rawText: string;
  workSiteId: string;
  headcount: string;
  budget: string;
  department: string;
  scheduleHours: string;
  requesterId: string;
}

function toForm(r?: KadryHiringRequest): FormState {
  return {
    title: r?.title ?? '',
    rawText: r?.raw_text ?? '',
    workSiteId: r?.work_site_id ?? '',
    headcount: String(r?.headcount ?? 1),
    budget: r?.budget_rub != null ? String(r.budget_rub) : '',
    department: r?.department ?? '',
    scheduleHours: String(r?.schedule_hours ?? 12),
    requesterId: r?.requester_id ?? '',
  };
}

/** Серверное поле `validation_error` → поле формы. */
const SERVER_FIELD: Record<string, keyof FormState> = {
  title: 'title',
  raw_text: 'rawText',
  work_site_id: 'workSiteId',
  headcount: 'headcount',
  budget_rub: 'budget',
  department: 'department',
  schedule_hours: 'scheduleHours',
  requester_id: 'requesterId',
};

/** Выбор заказчика: только назначающим (рекрутер, администратор подбора). */
export interface RequesterChoice {
  options: KadryStaffMember[];
  loading: boolean;
  /** Подпись для заказчика, которого нет в справочнике. */
  nameOf: (id: string) => string | undefined;
}

interface Props {
  open: boolean;
  /** Заявка для правки; без неё — создание. */
  request?: KadryHiringRequest;
  workSites: KadryWorkSite[];
  /** Поле бюджета — только при `admin_settings`. */
  showBudget: boolean;
  /**
   * Полный доступ к заявке (рекрутер/администратор): подразделение и период
   * повторного поиска. Заказчик в своём черновике меняет только название,
   * текст, объект и численность.
   */
  fullAccess: boolean;
  /** Выбор заказчика; `null` — поле не показывается. */
  requesterChoice: RequesterChoice | null;
  busy: boolean;
  error: unknown;
  onDismissError: () => void;
  /** «Обновить данные» после конфликта версий: перечитать заявку и закрыть форму. */
  onRefresh?: () => void;
  onCreate?: (body: KadryCreateRequest) => void;
  /** PATCH: изменённые поля и версия заявки, с которой форма была открыта. */
  onPatch?: (patch: KadryPatchRequest, baseVersion: number) => void;
  onClose: () => void;
}

/**
 * Форма заявки на подбор: создание и правка. При открытии запоминается снимок
 * заявки: PATCH содержит только отличия формы от снимка и `If-Match` его
 * версии. Если заявку тем временем изменили, сервер ответит `version_conflict`
 * — чужая правка не затирается; «Обновить данные» перечитывает заявку и
 * закрывает форму.
 */
export function RequestFormModal({
  open,
  request,
  workSites,
  showBudget,
  fullAccess,
  requesterChoice,
  busy,
  error,
  onDismissError,
  onRefresh,
  onCreate,
  onPatch,
  onClose,
}: Props) {
  const editing = Boolean(request);
  const [base, setBase] = useState<KadryHiringRequest | undefined>(request);
  const [f, setF] = useState<FormState>(() => toForm(request));
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (open) {
      setBase(request);
      setF(toForm(request));
      setTouched(false);
    }
    // Снимок — только при открытии: пока форма открыта, внешние обновления
    // заявки не должны ни затирать ввод, ни менять базу для сравнения.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Ошибка проверки на сервере — под своим полем (details.field).
  const serverField =
    kadryErrorCode(error) === 'validation_error'
      ? SERVER_FIELD[String(kadryErrorDetails(error).field ?? '')]
      : undefined;
  const serverReason = serverField ? String(kadryErrorDetails(error).reason ?? '') : '';
  const set = <K extends keyof FormState>(k: K, v: FormState[K]) => {
    setF((p) => ({ ...p, [k]: v }));
    // Серверная ошибка поля устарела, как только поле поправили.
    if (serverField) onDismissError();
  };

  // Выведенный из справочника объект остаётся в списке, если он уже выбран.
  const siteOptions = useMemo(
    () => workSites.filter((s) => s.active || s.id === f.workSiteId),
    [workSites, f.workSiteId],
  );

  const requesterOptions: ComboboxOption[] = useMemo(
    () =>
      (requesterChoice?.options ?? []).map((s) => ({
        id: s.user_id,
        label: s.full_name,
        sublabel: s.department ?? undefined,
      })),
    [requesterChoice?.options],
  );
  const requesterLabel = f.requesterId
    ? (requesterOptions.find((o) => o.id === f.requesterId)?.label ??
      requesterChoice?.nameOf(f.requesterId) ??
      f.requesterId)
    : undefined;

  const headcount = kadryParseNumber(f.headcount);
  const budget = kadryParseNumber(f.budget);
  const scheduleHours = kadryParseNumber(f.scheduleHours);
  const errors: Partial<Record<keyof FormState, string | null>> = {
    title:
      f.title.trim().length < 3 || f.title.trim().length > 200
        ? 'Название: от 3 до 200 символов'
        : null,
    rawText: f.rawText.length > 20_000 ? 'Текст: не длиннее 20 000 символов' : null,
    headcount:
      headcount == null ||
      !Number.isInteger(headcount) ||
      headcount < 1 ||
      headcount > 1000
        ? 'Численность: целое число от 1 до 1000'
        : null,
    budget:
      budget != null && (Number.isNaN(budget) || budget < 0 || budget > 1e10)
        ? 'Бюджет: число от 0'
        : null,
    scheduleHours:
      fullAccess &&
      (scheduleHours == null ||
        !Number.isInteger(scheduleHours) ||
        scheduleHours < 1 ||
        scheduleHours > 168)
        ? 'Период: целое число часов от 1 до 168'
        : null,
  };
  const invalid = Object.values(errors).some(Boolean);

  const fieldError = (k: keyof FormState) =>
    (touched && errors[k]) || (serverField === k ? serverReason : null);

  function buildPatch(r: KadryHiringRequest): KadryPatchRequest {
    const p: KadryPatchRequest = {};
    const title = f.title.trim();
    if (title !== r.title) p.title = title;
    const raw = f.rawText.trim();
    if (raw !== (r.raw_text ?? '').trim()) p.raw_text = raw || null;
    const ws = f.workSiteId || null;
    if (ws !== (r.work_site_id ?? null)) p.work_site_id = ws;
    if (headcount !== r.headcount) p.headcount = headcount;
    if (showBudget && budget !== (r.budget_rub ?? null)) p.budget_rub = budget;
    if (fullAccess) {
      const dept = f.department.trim() || null;
      if (dept !== (r.department ?? null)) p.department = dept;
      if (scheduleHours !== r.schedule_hours) p.schedule_hours = scheduleHours;
    }
    if (requesterChoice) {
      const req = f.requesterId || null;
      if (req !== (r.requester_id ?? null)) p.requester_id = req;
    }
    return p;
  }

  function submit(e: FormEvent) {
    e.preventDefault();
    setTouched(true);
    if (invalid || busy) return;
    if (base) {
      const patch = buildPatch(base);
      if (Object.keys(patch).length === 0) {
        onClose();
        return;
      }
      onPatch?.(patch, base.version);
    } else {
      onCreate?.({
        title: f.title.trim(),
        raw_text: f.rawText.trim() || null,
        work_site_id: f.workSiteId || null,
        headcount,
        budget_rub: showBudget ? budget : null,
        requester_id: requesterChoice ? f.requesterId || null : null,
        schedule_hours: fullAccess ? scheduleHours : null,
      });
    }
  }

  const conflict = kadryErrorCode(error) === 'version_conflict';

  return (
    <Modal
      open={open}
      title={editing ? 'Изменить заявку' : 'Новая заявка на подбор'}
      onClose={() => !busy && onClose()}
    >
      <form onSubmit={submit} noValidate>
        <Field>
          <Label htmlFor="kr-title">Название *</Label>
          <Input
            id="kr-title"
            value={f.title}
            maxLength={200}
            placeholder="Например: Сварщики НАКС на объект «Северный»"
            onChange={(e) => set('title', e.target.value)}
            autoFocus
          />
          {fieldError('title') && <KadryFieldError>{fieldError('title')}</KadryFieldError>}
        </Field>
        <Field>
          <Label htmlFor="kr-text">Текст заявки</Label>
          <KadryTextArea
            id="kr-text"
            value={f.rawText}
            maxLength={20_000}
            placeholder="Кого ищем, сколько, на какой срок, условия, обязательные допуски — в свободной форме."
            onChange={(e) => set('rawText', e.target.value)}
          />
          <KadryFieldHint>
            Текст хранится зашифрованным и виден только отделу кадров и автору заявки. По нему
            составляется профиль поиска.
          </KadryFieldHint>
          {fieldError('rawText') && <KadryFieldError>{fieldError('rawText')}</KadryFieldError>}
        </Field>
        {requesterChoice && (
          <Field>
            <Label htmlFor="kr-requester">Заказчик</Label>
            <Combobox
              id="kr-requester"
              value={f.requesterId}
              selectedLabel={requesterLabel}
              options={requesterOptions}
              onChange={(o) => set('requesterId', o?.id ?? '')}
              loading={requesterChoice.loading}
              placeholder="Начните вводить ФИО заказчика…"
              emptyText="Нет сотрудников с ролью заказчика"
            />
            <KadryFieldHint>
              Кому нужны люди: заказчик видит заявку и шорт-лист без персональных данных. Не
              выбран — заказчиком будете вы, если у вас есть роль заказчика.
            </KadryFieldHint>
            {fieldError('requesterId') && (
              <KadryFieldError>{fieldError('requesterId')}</KadryFieldError>
            )}
          </Field>
        )}
        <KadryFieldGrid>
          <Field>
            <Label htmlFor="kr-site">Объект</Label>
            <Select
              id="kr-site"
              value={f.workSiteId}
              onChange={(e) => set('workSiteId', e.target.value)}
            >
              <option value="">Не выбран</option>
              {siteOptions.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                  {s.active ? '' : ' (выведен)'}
                </option>
              ))}
            </Select>
            <KadryFieldHint>Место выполнения работ — из справочника объектов.</KadryFieldHint>
            {fieldError('workSiteId') && (
              <KadryFieldError>{fieldError('workSiteId')}</KadryFieldError>
            )}
          </Field>
          <Field>
            <Label htmlFor="kr-headcount">Численность *</Label>
            <Input
              id="kr-headcount"
              type="number"
              inputMode="numeric"
              min={1}
              max={1000}
              step={1}
              value={f.headcount}
              onChange={(e) => set('headcount', e.target.value)}
            />
            {fieldError('headcount') && (
              <KadryFieldError>{fieldError('headcount')}</KadryFieldError>
            )}
          </Field>
        </KadryFieldGrid>
        {(showBudget || fullAccess) && (
          <KadryFieldGrid>
            {showBudget && (
              <Field>
                <Label htmlFor="kr-budget">Бюджет подбора, ₽</Label>
                <Input
                  id="kr-budget"
                  type="number"
                  inputMode="decimal"
                  min={0}
                  step={100}
                  value={f.budget}
                  placeholder="Не задан"
                  onChange={(e) => set('budget', e.target.value)}
                />
                <KadryFieldHint>Лимит на платные действия площадок по этой заявке.</KadryFieldHint>
                {fieldError('budget') && <KadryFieldError>{fieldError('budget')}</KadryFieldError>}
              </Field>
            )}
            {fullAccess && (
              <Field>
                <Label htmlFor="kr-hours">Повторять поиск, ч</Label>
                <Input
                  id="kr-hours"
                  type="number"
                  inputMode="numeric"
                  min={1}
                  max={168}
                  step={1}
                  value={f.scheduleHours}
                  onChange={(e) => set('scheduleHours', e.target.value)}
                />
                <KadryFieldHint>Как часто активная заявка повторяет поиск (1–168 ч).</KadryFieldHint>
                {fieldError('scheduleHours') && (
                  <KadryFieldError>{fieldError('scheduleHours')}</KadryFieldError>
                )}
              </Field>
            )}
          </KadryFieldGrid>
        )}
        {fullAccess && !errors.scheduleHours && scheduleHours != null && (
          <ScheduleCost hours={scheduleHours} />
        )}
        {editing && fullAccess && (
          <Field>
            <Label htmlFor="kr-dept">Подразделение-заказчик</Label>
            <Input
              id="kr-dept"
              value={f.department}
              maxLength={200}
              placeholder="По умолчанию — подразделение заказчика"
              onChange={(e) => set('department', e.target.value)}
            />
            <KadryFieldHint>
              Рекрутеры видят заявки своего подразделения — смена подразделения меняет, кто видит
              заявку.
            </KadryFieldHint>
            {fieldError('department') && (
              <KadryFieldError>{fieldError('department')}</KadryFieldError>
            )}
          </Field>
        )}
        {Boolean(error) && !serverField && (
          <Field>
            <KadryErrorBanner error={error} onDismiss={onDismissError} onRefresh={onRefresh} />
          </Field>
        )}
        <KadryFormActions>
          <Button type="button" $variant="secondary" onClick={onClose} disabled={busy}>
            {conflict ? 'Закрыть' : 'Отмена'}
          </Button>
          {!conflict && (
            <Button type="submit" disabled={busy || (touched && invalid)}>
              {busy ? 'Сохраняем…' : editing ? 'Сохранить' : 'Создать заявку'}
            </Button>
          )}
        </KadryFormActions>
      </form>
    </Modal>
  );
}
